import { useState, type FormEvent } from 'react';

import { Button, Field, Input, MonoLabel, Textarea } from '@/design-system';
import {
    formatMoney,
    toDecimalAmount,
    type Invoice,
    type InvoiceLine,
    type InvoiceLineDraft,
} from '@/entities/invoice';

import { billingCopy } from '../copy';
import { describeBillingError } from '../errors';
import { useUpdateInvoice } from '../hooks';

export interface DraftInvoiceEditorProps {
    /** Het concept mét zijn regels (`GET /invoices/{uuid}`). */
    invoice: Invoice;
    lines: readonly InvoiceLine[];
    onSaved: (message: string) => void;
    onCancel: () => void;
}

/** Eén regel terwijl je hem typt: los van het model, want je mag hem leeg maken. */
interface LineForm {
    /** Blijft gelijk terwijl je typt, zodat React de velden niet door elkaar haalt. */
    key: string;
    description: string;
    quantity: string;
    unitPrice: string;
    vatRate: string;
    /** De dienst van de keuring die deze regel factureert; alleen ter informatie. */
    serviceName: string | null;
}

let nextKey = 0;

function toForm(line: InvoiceLine): LineForm {
    return {
        key: `line-${nextKey++}`,
        description: line.description,
        quantity: toInput(line.quantity),
        unitPrice: toInput(line.unitPrice),
        vatRate: toInput(line.vatRate),
        serviceName: line.serviceName,
    };
}

/** `"495.00"` -> `"495"`, `"1.50"` -> `"1,5"`: zoals je het zelf zou typen. */
function toInput(amount: string): string {
    const [whole = '0', fraction = ''] = amount.split('.');
    const decimals = fraction.replace(/0+$/, '');

    return decimals === '' ? whole : `${whole},${decimals}`;
}

/**
 * Een concept bewerken, tot het moment van versturen.
 *
 * Alles wat op de factuur staat is hier van jou: de omschrijving van een regel,
 * het aantal, het bedrag, het btw-tarief van díe regel, een extra regel voor
 * aanvullende kosten, het kenmerk, de tekst en de betaaltermijn. Verstuur je de
 * factuur, dan krijgt ze een nummer en ligt ze vast - dat staat er met zoveel
 * woorden boven.
 *
 * De totalen staan er wel bij, maar worden niet hier uitgerekend: de API telt op
 * (btw per regel afgerond, daarna gesommeerd) en de factuur die terugkomt is wat
 * de klant straks ziet. Dit scherm rekent nergens met `Number`.
 */
export function DraftInvoiceEditor({ invoice, lines, onSaved, onCancel }: DraftInvoiceEditorProps) {
    const update = useUpdateInvoice();

    const [rows, setRows] = useState<LineForm[]>(() => lines.map(toForm));
    const [reference, setReference] = useState(invoice.reference ?? '');
    const [notes, setNotes] = useState(invoice.notes ?? '');
    const [paymentTerm, setPaymentTerm] = useState(String(invoice.paymentTermDays));

    function change(key: string, field: keyof Omit<LineForm, 'key' | 'serviceName'>, value: string) {
        setRows((current) =>
            current.map((row) => (row.key === key ? { ...row, [field]: value } : row)),
        );
    }

    function submit(event: FormEvent) {
        event.preventDefault();

        const nextLines: InvoiceLineDraft[] = rows.map((row) => ({
            description: row.description.trim(),
            quantity: toDecimalAmount(row.quantity) || '1',
            unitPrice: toDecimalAmount(row.unitPrice),
            vatRate: toDecimalAmount(row.vatRate),
        }));

        const term = Number.parseInt(paymentTerm, 10);

        update.mutate(
            {
                invoiceUuid: invoice.uuid,
                changes: {
                    reference: reference.trim() === '' ? null : reference.trim(),
                    notes: notes.trim() === '' ? null : notes.trim(),
                    ...(Number.isNaN(term) ? {} : { paymentTermDays: term }),
                    lines: nextLines,
                },
            },
            { onSuccess: (saved) => onSaved(billingCopy.draftSaved(formatMoney(saved.total))) },
        );
    }

    return (
        <form
            onSubmit={submit}
            aria-label={billingCopy.editDraftLabel(invoice.label)}
            className="mt-3 grid gap-3 rounded-[9px] bg-[var(--surface-subtle)] p-3"
        >
            <p className="m-0 text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                {billingCopy.draftEditable}
            </p>

            <ul className="m-0 grid list-none gap-3 p-0">
                {rows.map((row, index) => (
                    <li
                        key={row.key}
                        className="grid gap-[10px] border-t border-[var(--border-subtle-2)] pt-3"
                    >
                        <div className="flex items-center justify-between gap-2">
                            <MonoLabel size={10} variant="value" tone="muted">
                                {row.serviceName
                                    ? billingCopy.lineFromService(row.serviceName)
                                    : billingCopy.lineNumber(index + 1)}
                            </MonoLabel>
                            <Button
                                type="button"
                                variant="ghost"
                                size="xs"
                                icon="delete"
                                disabled={rows.length === 1}
                                aria-label={billingCopy.removeLine(index + 1)}
                                onClick={() =>
                                    setRows((current) =>
                                        current.filter((item) => item.key !== row.key),
                                    )
                                }
                            >
                                {billingCopy.removeLine(index + 1)}
                            </Button>
                        </div>

                        <Field label={billingCopy.lineDescription} size="sm">
                            <Input
                                value={row.description}
                                required
                                maxLength={255}
                                placeholder={billingCopy.lineDescriptionPlaceholder}
                                onChange={(event) =>
                                    change(row.key, 'description', event.target.value)
                                }
                            />
                        </Field>

                        <div className="grid grid-cols-[repeat(auto-fit,minmax(104px,1fr))] gap-[10px]">
                            <Field label={billingCopy.lineQuantity} size="sm">
                                <Input
                                    mono
                                    inputMode="decimal"
                                    value={row.quantity}
                                    required
                                    onChange={(event) =>
                                        change(row.key, 'quantity', event.target.value)
                                    }
                                />
                            </Field>
                            <Field
                                label={billingCopy.lineUnitPrice}
                                size="sm"
                                description={billingCopy.amountHelp}
                            >
                                <Input
                                    mono
                                    inputMode="decimal"
                                    value={row.unitPrice}
                                    required
                                    onChange={(event) =>
                                        change(row.key, 'unitPrice', event.target.value)
                                    }
                                />
                            </Field>
                            <Field label={billingCopy.lineVat} size="sm">
                                <Input
                                    mono
                                    inputMode="decimal"
                                    value={row.vatRate}
                                    required
                                    onChange={(event) =>
                                        change(row.key, 'vatRate', event.target.value)
                                    }
                                />
                            </Field>
                        </div>
                    </li>
                ))}
            </ul>

            {rows.length === 1 ? (
                <p className="m-0 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {billingCopy.lastLine}
                </p>
            ) : null}

            <div>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    icon="add"
                    onClick={() =>
                        setRows((current) => [
                            ...current,
                            {
                                key: `line-${nextKey++}`,
                                description: '',
                                quantity: '1',
                                unitPrice: '',
                                vatRate: current.at(-1)?.vatRate ?? '21',
                                serviceName: null,
                            },
                        ])
                    }
                >
                    {billingCopy.addLine}
                </Button>
                <p className="m-0 mt-1 text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                    {billingCopy.addLineHint}
                </p>
            </div>

            <Field label={billingCopy.referenceLabel} size="sm">
                <Input
                    value={reference}
                    maxLength={255}
                    placeholder={billingCopy.referencePlaceholder}
                    onChange={(event) => setReference(event.target.value)}
                />
            </Field>

            <Field label={billingCopy.notesLabel} size="sm">
                <Textarea
                    rows={3}
                    value={notes}
                    maxLength={2000}
                    placeholder={billingCopy.notesPlaceholder}
                    onChange={(event) => setNotes(event.target.value)}
                />
            </Field>

            <Field
                label={billingCopy.paymentTerm}
                size="sm"
                description={billingCopy.paymentTermHelp}
                className="max-w-[180px]"
            >
                <Input
                    mono
                    inputMode="numeric"
                    value={paymentTerm}
                    onChange={(event) => setPaymentTerm(event.target.value)}
                />
            </Field>

            <div className="rounded-[9px] border border-[var(--border-subtle-2)] px-3 py-2">
                <MonoLabel size={11} variant="value" tone="muted">
                    {billingCopy.newTotal(
                        formatMoney(invoice.subtotal),
                        formatMoney(invoice.vatAmount),
                        formatMoney(invoice.total),
                    )}
                </MonoLabel>
            </div>

            {update.isError ? (
                <p role="alert" className="m-0 text-[12px] leading-[1.4] text-[var(--danger)]">
                    {describeBillingError(update.error)}
                </p>
            ) : null}

            <div className="flex flex-wrap gap-2">
                <Button type="submit" variant="primary" size="sm" disabled={update.isPending}>
                    {update.isPending ? billingCopy.savingDraft : billingCopy.saveDraft}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={onCancel}>
                    {billingCopy.discardChanges}
                </Button>
            </div>
        </form>
    );
}
